// src/pages/BrokerDashboard.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { getFirestore, collection, onSnapshot, query, orderBy } from "firebase/firestore";
import app from "../firebase";
import { Helmet } from "react-helmet-async";
import {
  FiHome,
  FiPlayCircle,
  FiFileText,
  FiHelpCircle,
  FiUser,
  FiExternalLink,
} from "react-icons/fi";

const db = getFirestore(app);

const brand = {
  bg: "bg-[#f5f8fc]",
  text: "text-[#0f2437]",
  btnPrimary: "bg-[#002b5c] text-white hover:bg-[#082f6d]",
  btnInverted: "bg-white text-[#002b5c] hover:bg-white/90 border border-white/20 shadow-sm",
  card: "bg-white border border-gray-200 shadow-sm rounded-2xl",
};

function SidebarLink({ active, onClick, icon: Icon, children }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left flex items-center gap-3 rounded-xl px-3 py-2 mb-1 transition
        ${active ? "bg-[#002b5c] text-white" : "hover:bg-gray-100 text-gray-800"}`}
    >
      <Icon className="text-lg shrink-0" />
      <span>{children}</span>
    </button>
  );
}

// Placeholder FAQ content — could move to Firestore later the same way
// Tutorials/Resources did, if you want admins editing this too.

const FAQS = [
  {
    q: "How do I get matched with new leads?",
    a: "Leads are routed automatically based on your licensed states and specialties. You'll see new matches in your dashboard.",
  },
  {
    q: "How do I update my licensing information?",
    a: "Reach out to support for now — self-service profile editing is coming soon.",
  },
  {
    q: "Who do I contact with questions?",
    a: "Email support or call the main office line listed in the Broker Handbook.",
  },
];

export default function BrokerDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [tutorials, setTutorials] = useState([]);
  const [resources, setResources] = useState([]);
  const [loadingContent, setLoadingContent] = useState(true);
  const navigate = useNavigate();
  const auth = getAuth(app);

  useEffect(() => {
    const unsubTutorials = onSnapshot(
      query(collection(db, "broker_tutorials"), orderBy("order", "asc")),
      (snap) => setTutorials(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error(err)
    );
    const unsubResources = onSnapshot(
      query(collection(db, "broker_resources"), orderBy("order", "asc")),
      (snap) => {
        setResources(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingContent(false);
      },
      (err) => {
        console.error(err);
        setLoadingContent(false);
      }
    );
    return () => {
      unsubTutorials();
      unsubResources();
    };
  }, []);

  return (
    <div className={`min-h-screen ${brand.bg} ${brand.text}`}>
      <Helmet>
        <title>Broker Dashboard • Your Choice Associates</title>
      </Helmet>

      {/* Topbar */}
      <header className="sticky top-0 z-40 bg-[#002b5c] text-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <h1 className="text-lg font-semibold">Broker Dashboard</h1>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-white/80">
              {auth.currentUser?.email}
            </span>
            <button
              onClick={async () => {
                await signOut(auth);
                navigate("/broker-login");
              }}
              className={`rounded-lg px-3 py-2 ${brand.btnInverted}`}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 grid md:grid-cols-[240px,1fr] gap-6">
        {/* Sidebar */}
        <aside className="hidden md:block sticky top-16 self-start">
          <div className={`${brand.card} p-3`}>
            <SidebarLink active={activeTab === "overview"} onClick={() => setActiveTab("overview")} icon={FiHome}>
              Overview
            </SidebarLink>
            <SidebarLink active={activeTab === "tutorials"} onClick={() => setActiveTab("tutorials")} icon={FiPlayCircle}>
              Tutorials
            </SidebarLink>
            <SidebarLink active={activeTab === "resources"} onClick={() => setActiveTab("resources")} icon={FiFileText}>
              Resources
            </SidebarLink>
            <SidebarLink active={activeTab === "faq"} onClick={() => setActiveTab("faq")} icon={FiHelpCircle}>
              FAQ
            </SidebarLink>
            <SidebarLink active={activeTab === "profile"} onClick={() => setActiveTab("profile")} icon={FiUser}>
              Profile
            </SidebarLink>
          </div>
        </aside>

        {/* Content */}
        <section>
          {/* Mobile Tabs */}
          <div className="md:hidden flex gap-2 mb-4 flex-wrap">
            {["overview", "tutorials", "resources", "faq", "profile"].map((t) => (
              <button
                key={t}
                onClick={() => setActiveTab(t)}
                className={`px-3 py-2 rounded-xl text-sm transition ${
                  activeTab === t ? "bg-[#002b5c] text-white" : "bg-white"
                } border border-gray-200`}
              >
                {t[0].toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          {activeTab === "overview" && (
            <div className={`${brand.card} p-6`}>
              <h2 className="text-xl font-semibold mb-2">Welcome to Your Choice Associates</h2>
              <p className="text-gray-600 mb-4">
                You're approved and ready to go. Start with the Tutorials tab if you're new,
                or check Resources for handbooks and marketing materials.
              </p>
              <div className="grid sm:grid-cols-3 gap-4 mt-6">
                <div className="border border-gray-200 rounded-xl p-4">
                  <p className="text-sm text-gray-500">Tutorials available</p>
                  <p className="text-2xl font-bold text-[#002b5c]">{tutorials.length}</p>
                </div>
                <div className="border border-gray-200 rounded-xl p-4">
                  <p className="text-sm text-gray-500">Resources available</p>
                  <p className="text-2xl font-bold text-[#002b5c]">{resources.length}</p>
                </div>
                <div className="border border-gray-200 rounded-xl p-4">
                  <p className="text-sm text-gray-500">Account status</p>
                  <p className="text-2xl font-bold text-green-600">Approved</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === "tutorials" && (
            <div className={`${brand.card} p-6`}>
              <h2 className="text-xl font-semibold mb-1">Tutorials</h2>
              <p className="text-sm text-gray-500 mb-5">
                Short walkthroughs to help you get productive quickly.
              </p>
              <div className="space-y-3">
                {loadingContent ? (
                  <div className="text-gray-500 text-sm">Loading tutorials…</div>
                ) : tutorials.length === 0 ? (
                  <div className="text-gray-500 text-sm">No tutorials yet — check back soon.</div>
                ) : (
                  tutorials.map((t) => {
                    const content = (
                      <>
                        <div className="w-12 h-12 rounded-full bg-[#002b5c]/10 text-[#002b5c] grid place-items-center shrink-0">
                          <FiPlayCircle className="text-xl" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium">{t.title}</p>
                          <p className="text-sm text-gray-500">{t.description}</p>
                        </div>
                        {t.duration && (
                          <span className="ml-auto text-xs text-gray-400 shrink-0">{t.duration}</span>
                        )}
                        {t.videoUrl && <FiExternalLink className="text-gray-400 shrink-0" />}
                      </>
                    );
                    return t.videoUrl ? (
                      <a
                        key={t.id}
                        href={t.videoUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="border border-gray-200 rounded-xl p-4 flex items-center gap-4 hover:bg-gray-50 transition"
                      >
                        {content}
                      </a>
                    ) : (
                      <div
                        key={t.id}
                        className="border border-gray-200 rounded-xl p-4 flex items-center gap-4"
                      >
                        {content}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {activeTab === "resources" && (
            <div className={`${brand.card} p-6`}>
              <h2 className="text-xl font-semibold mb-1">Resources</h2>
              <p className="text-sm text-gray-500 mb-5">
                Documents, checklists, and marketing materials.
              </p>
              <div className="space-y-3">
                {loadingContent ? (
                  <div className="text-gray-500 text-sm">Loading resources…</div>
                ) : resources.length === 0 ? (
                  <div className="text-gray-500 text-sm">No resources yet — check back soon.</div>
                ) : (
                  resources.map((r) => {
                    const content = (
                      <>
                        <div className="w-12 h-12 rounded-full bg-[#e31c3d]/10 text-[#e31c3d] grid place-items-center shrink-0">
                          <FiFileText className="text-xl" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium">{r.title}</p>
                          <p className="text-sm text-gray-500">{r.note}</p>
                        </div>
                        {r.fileUrl && <FiExternalLink className="text-gray-400 shrink-0 ml-auto" />}
                      </>
                    );
                    return r.fileUrl ? (
                      <a
                        key={r.id}
                        href={r.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="border border-gray-200 rounded-xl p-4 flex items-center gap-4 hover:bg-gray-50 transition"
                      >
                        {content}
                      </a>
                    ) : (
                      <div key={r.id} className="border border-gray-200 rounded-xl p-4 flex items-center gap-4">
                        {content}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {activeTab === "faq" && (
            <div className={`${brand.card} p-6`}>
              <h2 className="text-xl font-semibold mb-4">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {FAQS.map((f, i) => (
                  <div key={i} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                    <p className="font-medium mb-1">{f.q}</p>
                    <p className="text-sm text-gray-600">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "profile" && (
            <div className={`${brand.card} p-6`}>
              <h2 className="text-xl font-semibold mb-2">Profile</h2>
              <p className="text-gray-600 text-sm mb-4">
                Signed in as <strong>{auth.currentUser?.email}</strong>
              </p>
              <p className="text-sm text-gray-500">
                Self-service profile editing isn't built yet — let your admin know if you need
                anything updated.
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
